/**
 * NOTIFICATION BELL COMPONENT
 * 
 * Shows "Notifications" text with unread count badge.
 * Plays a sound when new notifications arrive.
 * Dropdown shows recent notifications with links.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { X } from 'lucide-react';
import { AppIcons } from '../constants/icons';

interface Notification {
  id: string;
  type: 'wishlist_alert' | 'destash_inquiry';
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: Timestamp;
  data?: Record<string, unknown>;
}

interface NotificationBellProps {
  userId: string | null;
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userId, className = '' }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const previousCountRef = useRef(0);
  const isInitialLoad = useRef(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Subscribe to notifications
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Reset refs for new user
    isInitialLoad.current = true;
    previousCountRef.current = 0;

    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs: Notification[] = [];
      let unread = 0;

      snapshot.forEach((doc) => {
        const data = doc.data() as Omit<Notification, 'id'>;
        notifs.push({ id: doc.id, ...data });
        if (!data.read) unread++;
      });

      // Play sound if new unread notifications (skip initial load)
      console.log('[NotificationBell] unread:', unread, 'previousCount:', previousCountRef.current, 'isInitialLoad:', isInitialLoad.current);
      if (unread > previousCountRef.current && !isInitialLoad.current) {
        console.log('[NotificationBell] Playing sound!');
        playNotificationSound();
      }

      // Mark initial load as complete after first snapshot
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }

      setNotifications(notifs);
      setUnreadCount(unread);
      previousCountRef.current = unread;
    });

    return () => unsubscribe();
  }, [userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Play notification sound using mp3 file
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().then(() => {
        console.log('[NotificationBell] Sound played successfully');
      }).catch((err) => {
        console.log('[NotificationBell] Audio play failed:', err.message);
      });
    } catch (err) {
      console.log('[NotificationBell] Audio error:', err);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!userId) return;
    
    try {
      const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
      await updateDoc(notifRef, { read: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!userId) return;
    
    try {
      const unreadNotifs = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifs.map(notif => {
          const notifRef = doc(db, 'users', userId, 'notifications', notif.id);
          return updateDoc(notifRef, { read: true });
        })
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp: Timestamp): string => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get icon component for notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'wishlist_alert': return AppIcons.WishlistAlert;
      case 'destash_inquiry': return AppIcons.DestashInquiry;
      default: return AppIcons.Notification;
    }
  };

  if (!userId) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Text Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative transition-default font-body ${className}`}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        Notifications
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-3 badge-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 glass-modal border border-slate-600 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 dropdown-header">
            <h3 className="text-glass font-bold font-heading">
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-glass-secondary hover:text-glass text-xs underline font-body"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-glass-secondary hover:text-glass"
              >
                <X className="icon-sm" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-glass-muted font-body">
                <AppIcons.Notification className="icon-2xl mx-auto mb-2 !text-white/60" aria-label="No notifications" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`border-b border-slate-600 last:border-0 ${
                    notif.read ? 'list-item-read' : 'list-item-unread'
                  }`}
                >
                  {(() => {
                    const IconComponent = getNotificationIcon(notif.type);
                    const iconElement = <IconComponent className="icon-lg flex-shrink-0 !text-white" aria-label={notif.type} />;

                    return notif.link ? (
                      <Link
                        to={notif.link}
                        onClick={() => {
                          markAsRead(notif.id);
                          setIsOpen(false);
                        }}
                        className="block px-4 py-3 list-item-hover"
                      >
                        <div className="flex gap-3">
                          {iconElement}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-glass font-body">
                              {notif.title}
                            </p>
                            <p className="text-sm text-glass-secondary line-clamp-2 font-body">
                              {notif.message}
                            </p>
                            <p className="text-xs text-glass-muted mt-1">
                              {formatTimeAgo(notif.createdAt)}
                            </p>
                          </div>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-fuchsia-400 rounded-full flex-shrink-0 mt-2"></span>
                          )}
                        </div>
                      </Link>
                    ) : (
                      <div
                        className="px-4 py-3 cursor-pointer list-item-hover"
                        onClick={() => markAsRead(notif.id)}
                      >
                        <div className="flex gap-3">
                          {iconElement}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-glass font-body">
                              {notif.title}
                            </p>
                            <p className="text-sm text-glass-secondary line-clamp-2 font-body">
                              {notif.message}
                            </p>
                            <p className="text-xs text-glass-muted mt-1">
                              {formatTimeAgo(notif.createdAt)}
                            </p>
                          </div>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-fuchsia-400 rounded-full flex-shrink-0 mt-2"></span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
